import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { getCheckoutSessionStatus } from '../utils/api';
import { sendGoogleAdsConversion } from '../utils/googleAds';

export default function CheckoutReturn() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshUserProfile } = useAuth();
  const { theme } = useTheme();
  const [status, setStatus] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [amountTotal, setAmountTotal] = useState(null);
  const [currency, setCurrency] = useState('USD');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setError('Missing session');
      return;
    }
    getCheckoutSessionStatus(sessionId)
      .then((data) => {
        setStatus(data.status);
        setCustomerEmail(data.customer_email || '');
        if (data.status === 'complete') {
          setAmountTotal(data.amount_total);
          setCurrency((data.currency || 'USD').toUpperCase());
          refreshUserProfile();
        }
      })
      .catch((err) => setError(err.message || 'Could not verify payment'));
  }, [sessionId, refreshUserProfile]);

  useEffect(() => {
    if (status === 'open') {
      navigate('/checkout?plan=pro', { replace: true });
    }
  }, [status, navigate]);

  useEffect(() => {
    if (status === 'complete') {
      sendGoogleAdsConversion('purchase', {
        value: amountTotal != null ? amountTotal / 100 : 0,
        currency,
        transactionId: sessionId,
        dedupeKey: `purchase:${sessionId}`,
        email: customerEmail,
      });
    }
  }, [status, amountTotal, currency, sessionId, customerEmail]);

  if (error) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: theme.usageError, marginBottom: 16 }}>{error}</p>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{
              padding: '12px 20px',
              fontSize: 15,
              fontWeight: 600,
              color: theme.primaryContrast,
              backgroundColor: theme.primary,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Back to profile
          </button>
        </div>
      </div>
    );
  }

  if (status === 'complete') {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text, padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ maxWidth: 420, textAlign: 'center' }}>
          <h2 style={{ marginBottom: 12 }}>Thank you for subscribing</h2>
          <p style={{ color: theme.textMuted, marginBottom: 8 }}>
            A confirmation has been sent to {customerEmail || 'your email'}.
          </p>
          <p style={{ color: theme.textMuted, fontSize: 14, marginBottom: 24 }}>
            Your plan is active. You will not be charged again until the end of your billing period.
          </p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              padding: '12px 24px',
              fontSize: 15,
              fontWeight: 600,
              color: theme.primaryContrast,
              backgroundColor: theme.primary,
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
            }}
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, border: `3px solid ${theme.border}`, borderTopColor: theme.primary, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
